import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { panel } from './panelTheme';

// IL CAMPO NUMERICO CON LO STEPPER — il valore che si scrive, e due frecce per chi non vuole
// scriverlo.
//
// Un campo solo per tutti i numeri del pannello: quello accanto a uno slider, quello di un asse,
// quello da solo. Prima erano tre disegni diversi — un `input[type=number]` largo 90 e centrato
// accanto agli slider, le frecce native altrove — e due numeri sulla stessa colonna non si
// allineavano.
//
// AL CENTRO, COME NEL PROTOTIPO (utente, 2026-10-20): il valore e la sua unità in mezzo al campo. L'unità sta
// dentro il campo perché il grado non è un'etichetta della riga, è parte del valore.
//
// LE FRECCE SONO UN ACCELERATORE, non l'unico modo. Sono strette, sotto il minimo di WCAG 2.5.8: vale
// l'eccezione equivalente, perché la stessa cosa si ottiene dal campo — che accetta il numero digitato,
// risponde a Su/Giù e si trascina. Per questo hanno `tabindex={-1}` e sono nascoste a chi legge con lo schermo.
//
// FUORI FUOCO SI TRASCINA, DENTRO FUOCO SI SCRIVE — è la regola che i due cursori dichiarano,
// `ew-resize` e `text`, ed è quella del mockup. Il clic non trascina: dà il fuoco, e da lì il campo
// è un campo. Il trascinamento parte dopo 3px, così una mano ferma resta un clic. Senza intervallo ogni
// pixel vale un passo; con l'intervallo il campo è uno slider, e il filo arancio segue il puntatore.
//
// CIÒ CHE SI SCRIVE SI CONSEGNA ALL'USCITA (utente, 2026-10-14 — round 4, decisione 7): con Invio o
// uscendo dal campo. Consegnato a ogni tasto, battere «150» spostava l'oggetto a 1, a 15 e a 150, e
// sulla scala uno «0» iniziale lo schiacciava. Esc rimette il valore di prima. Frecce, tasti Su/Giù,
// trascinamento e lo slider accanto restano immediati: sono gesti che si guardano mentre si fanno.

const FIELD_WIDTH = '78px';
const FIELD_HEIGHT = '24px';
const SCRUB_THRESHOLD = 3;

// LA FORMA DEL PROTOTIPO (`.num`, utente 2026-10-20): il valore al centro con la sua unità, una freccia per lato
// che compare al passaggio, e in un vettore l'asse come una linea colorata sotto il campo invece della lettera.
const AXIS_COLOR = { x: panel.axisX, y: panel.axisY, z: panel.axisZ } as const;

const Field = styled.span<{ $full?: boolean; $axis?: 'x' | 'y' | 'z'; $fill?: number }>`
    position: relative;
    display: flex;
    align-items: stretch;
    flex: ${(props) => (props.$full ? '1 1 auto' : '0 0 auto')};
    width: ${(props) => (props.$full ? 'auto' : FIELD_WIDTH)};
    min-width: 0;
    height: ${FIELD_HEIGHT};
    border: 1px solid var(--editor-field-line, ${panel.line});
    border-radius: 4px;
    background: ${panel.field};
    overflow: hidden;
    /* SI TRASCINA DA TUTTO IL CAMPO (prototipo), non solo dalle cifre: il cursore lo dice ovunque. */
    cursor: ew-resize;
    user-select: none;
    touch-action: pan-y;

    &:hover {
        border-color: var(--editor-faint, ${panel.inkMuted});
    }

    &:focus-within {
        border-color: ${panel.accent};
    }

    /* CON UN INTERVALLO IL CAMPO È ANCHE LO SLIDER (prototipo, num.ranged; utente, 2026-10-20): lavato d'arancio
       fino al valore, un filo arancio sul punto esatto. Si trascina come ogni campo, si clicca per scrivere. */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: ${(props) => (props.$fill === undefined ? 0 : props.$fill)}%;
        background: ${(props) => (props.$fill === undefined ? 'transparent' : 'var(--editor-accent-wash, rgb(232 101 10 / 8%))')};
        box-shadow: ${(props) => (props.$fill === undefined || props.$fill === 0 ? 'none' : `inset -2px 0 0 ${panel.accent}`)};
        pointer-events: none;
    }

    &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 2px;
        background: ${(props) => (props.$axis ? AXIS_COLOR[props.$axis] : 'transparent')};
        pointer-events: none;
    }

    /* SPENTO, IL CAMPO PERDE LA PROPRIA SUPERFICIE. Il fondo incassato è ciò che dice «qui si
       scrive»: un campo che non si può scrivere e se lo tiene continua a invitare. */
    &:has(input:disabled) {
        background: transparent;
        cursor: not-allowed;
    }
`;

// LA LETTERA DELL'ASSE RESTA DENTRO IL CAMPO (utente, 2026-10-20), nel colore del gizmo, oltre alla linea sotto: il
// colore da solo non basta a chi non lo distingue.
const AxisLetter = styled.span<{ $axis: 'x' | 'y' | 'z' }>`
    position: relative;
    flex-shrink: 0;
    align-self: center;
    padding: 0 2px;
    font-size: 10px;
    font-weight: 600;
    color: ${(props) => AXIS_COLOR[props.$axis]};
    pointer-events: none;
`;

// SOPRA IL RIEMPIMENTO: frecce, lettera e valore sono posizionati, così il lavato dello slider (::before, assoluto)
// passa sotto di loro invece di velarli.
const Middle = styled.span`
    position: relative;
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-width: 0;
`;

// `touch-action: pan-y`: su uno schermo a tocco lo scorrimento verticale resta al pannello, e il
// gesto orizzontale arriva al campo. Largo quanto quello che c'è scritto, così il valore e la sua
// unità stanno insieme al centro.
const ValueInput = styled.input`
    width: 100%;
    min-width: 1ch;
    max-width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    text-align: center;
    color: ${panel.ink};
    cursor: inherit;

    @supports (field-sizing: content) {
        width: auto;
        field-sizing: content;
    }

    &:focus {
        outline: none;
        cursor: text;
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;

const Unit = styled.span`
    flex-shrink: 0;
    font-size: 11px;
    color: var(--editor-faint, ${panel.inkMuted});
    pointer-events: none;
`;

// LE FRECCE STANNO AI DUE CAPI e compaiono al passaggio o col fuoco: a riposo il campo mostra solo il valore.
const Step = styled.button`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 16px;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--editor-faint, ${panel.inkMuted});
    cursor: pointer;
    opacity: 0;
    transition:
        opacity 110ms ease,
        background 110ms ease,
        color 110ms ease;

    ${Field}:hover > &,
    ${Field}:focus-within > & {
        opacity: 1;
    }

    &:hover {
        background: var(--editor-sunken, #e9ebed);
        color: ${panel.ink};
    }

    &:active {
        background: var(--editor-sunken, #e9ebed);
    }

    /* Spento col campo: due frecce vive su un campo morto sono una promessa. */
    ${Field}:has(input:disabled) > & {
        display: none;
    }

    svg {
        width: 8px;
        height: 8px;
        pointer-events: none;
    }
`;

const ChevronLeft = () => (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round'>
        <path d='m15 5-7 7 7 7' />
    </svg>
);

const ChevronRight = () => (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round'>
        <path d='m9 5 7 7-7 7' />
    </svg>
);

export type PropertiesPanelNumberFieldProps = {
    id?: string;
    value: number;
    min?: number;
    max?: number;
    step?: number;
    /** Il suffisso dentro il campo: «°», «m». Parte del valore, non della riga. */
    unit?: string;
    /** L'asse, col colore dei gizmo: una linea sotto il campo. */
    axis?: 'x' | 'y' | 'z';
    /** Il nome dell'asse per chi non vede il colore, quando non è l'asse: sulle coordinate di una texture X e Y si chiamano U e V. */
    letter?: string;
    /** Occupa tutta la larghezza disponibile invece dei 78px della colonna dei valori. */
    full?: boolean;
    /** L'intervallo dello slider: il campo si riempie fino al valore, e trascinarlo lo percorre. */
    range?: { min: number; max: number };
    ariaLabel?: string;
    onChange?: (value: number) => void;
    disabled?: boolean;
    /**
     * IL CAMPO PUÒ RESTARE VUOTO, quando vuoto vuol dire qualcosa («nessun limite»): mostra il segnaposto invece del
     * valore, e cancellare il testo chiama `onEmpty`. Frecce e trascinamento ripartono da `value`.
     */
    empty?: boolean;
    placeholder?: string;
    onEmpty?: () => void;
    /**
     * CONSEGNA A OGNI TASTO, in una finestra che ha il suo Confirm: lì il valore va letto subito, e chi scrive e preme
     * Confirm non deve perdere ciò che ha scritto. Nell'Inspector no: lì si consegna uscendo (vedi sopra).
     */
    live?: boolean;
};

export const PropertiesPanelNumberField: React.FC<PropertiesPanelNumberFieldProps> = ({
    id,
    value,
    min,
    max,
    step,
    unit,
    axis,
    letter,
    full,
    range,
    ariaLabel,
    onChange,
    disabled,
    empty,
    placeholder,
    onEmpty,
    live
}) => {
    // QUANTE CIFRE SI MOSTRANO LO DICE IL PASSO. Con un passo da un centesimo, «-0,000123» non è
    // più preciso: è solo più lungo, e in una colonna di tre campi non ci sta — si leggeva
    // «-0,0(», cioè non si leggeva affatto. Con lo stesso passo, «0» va scritto «0.00»: due
    // numeri incolonnati si confrontano dalla virgola, e senza gli zeri finali la virgola non
    // sta sotto la virgola.
    //
    // PER QUESTO IL CAMPO È DI TESTO e non `type=number`: un campo numerico tiene un NUMERO, e un
    // numero non sa di avere due decimali — `0.00` ci rientra come zero e si ridisegna «0». Le
    // cifre di troppo le tolgono già il nostro stepper e la validazione qui sotto; quello che si
    // perde è la tastiera numerica del telefono, che `inputMode` restituisce.
    //
    // MENTRE SI SCRIVE COMANDA CHI SCRIVE: finché il campo ha il fuoco si mostra quello che si
    // batte — battendo «0.» un campo riformattato lo rimangerebbe prima della cifra dopo.
    const [draft, setDraft] = useState<string | null>(null);

    // Il trascinamento in corso. Non è stato: non ridisegna niente, il valore arriva da `onChange`
    // come arriva dalle frecce.
    const scrub = useRef<{ x: number; from: number; pointer: number; moving: boolean; perPixel: number } | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const decimalsOf = (amount: number) => (String(amount).split('.')[1] ?? '').length;
    const decimals = step === undefined ? undefined : decimalsOf(step);
    const shown = draft ?? (empty ? '' : decimals === undefined ? String(value) : value.toFixed(decimals));

    // Frecce, tasti e trascinamento consegnano da qui: dentro gli estremi e alla finezza del passo.
    // Un passo da 0.05 sommato in virgola mobile dà 1.0500000000000003: si arrotonda alla stessa
    // finezza del passo, che è quella con cui il valore è stato dichiarato.
    const clamp = (next: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, next));

    const deliver = (next: number) => {
        const decimals = decimalsOf(step ?? 1);
        const delivered = decimals ? Number(clamp(next).toFixed(decimals)) : clamp(next);
        onChange?.(delivered);
        return delivered;
    };

    const nudge = (direction: 1 | -1) => deliver(value + direction * (step ?? 1));

    // TRASCINANDO UNO SLIDER IL FILO SEGUE IL PUNTATORE (prototipo; utente, 2026-10-20): un pixel vale l'intervallo
    // diviso per la larghezza del campo, arrotondato al passo. A un passo per pixel il filo correva più del mouse.
    const scrubStep = step ?? 1;
    const fill = range && range.max > range.min ? Math.min(100, Math.max(0, ((value - range.min) / (range.max - range.min)) * 100)) : undefined;

    // Si consegna solo un numero, e solo se è cambiato: «-», «0.» e il campo vuoto non sono numeri,
    // e restano al valore di prima.
    const parseDraft = (text: string) => {
        const parsed = Number(text.replace(',', '.'));
        return text.trim() === '' || isNaN(parsed) ? null : parsed;
    };

    // ENTRARE E USCIRE SENZA SCRIVERE NON CAMBIA NIENTE: il campo mostra il valore alla finezza del passo, e
    // riconsegnare quel testo arrotondava il valore vero (0.85 a passo 0.1 diventava 0.9).
    const focusedText = useRef<string | null>(null);

    const commit = () => {
        if (draft === focusedText.current) return;
        if (onEmpty && draft !== null && draft.trim() === '') return onEmpty();
        const parsed = draft === null ? null : parseDraft(draft);
        // Ciò che si scrive arriva com'è, dentro gli estremi: la finezza del passo vale per i gesti.
        if (parsed !== null && (empty || clamp(parsed) !== value)) onChange?.(clamp(parsed));
    };

    return (
        <Field
            $full={full}
            $axis={axis}
            $fill={fill}
            onPointerDown={(event) => {
                const input = inputRef.current;
                // Le frecce fanno da sé; dentro un campo che si sta scrivendo il puntatore seleziona il testo.
                if (!input || disabled || event.button !== 0 || (event.target as Element).closest('button')) return;
                if (document.activeElement === input && event.target === input) return;

                // Niente fuoco e niente cursore di testo finché non si sa se è un clic o un trascinamento. La cattura
                // subito, e non a trascinamento partito: così il rilascio arriva qui anche fuori dal campo, e un
                // trascinamento non resta mai a metà.
                event.preventDefault();
                event.currentTarget.setPointerCapture(event.pointerId);
                const width = event.currentTarget.getBoundingClientRect().width;
                scrub.current = {
                    x: event.clientX,
                    from: value,
                    pointer: event.pointerId,
                    moving: false,
                    perPixel: range && range.max > range.min && width ? (range.max - range.min) / width : 0
                };
            }}
            onPointerMove={(event) => {
                const drag = scrub.current;
                if (!drag || event.pointerId !== drag.pointer) return;

                const dx = event.clientX - drag.x;
                if (!drag.moving && Math.abs(dx) < SCRUB_THRESHOLD) return;

                drag.moving = true;
                deliver(drag.perPixel ? Math.round((drag.from + dx * drag.perPixel) / scrubStep) * scrubStep : drag.from + Math.round(dx) * scrubStep);
            }}
            onPointerUp={() => {
                const drag = scrub.current;
                scrub.current = null;
                if (drag && !drag.moving) inputRef.current?.focus();
            }}
            onPointerCancel={() => {
                scrub.current = null;
            }}
        >
            <Step type='button' tabIndex={-1} aria-hidden='true' disabled={disabled} onClick={() => nudge(-1)}>
                <ChevronLeft />
            </Step>
            {axis && <AxisLetter $axis={axis}>{letter ?? axis.toUpperCase()}</AxisLetter>}
            <Middle>
                <ValueInput
                    ref={inputRef}
                    id={id}
                    type='text'
                    inputMode='decimal'
                    placeholder={placeholder}
                    value={shown}
                    disabled={disabled}
                    aria-label={ariaLabel ?? (axis ? (letter ?? axis.toUpperCase()) : undefined)}
                    role='spinbutton'
                    aria-valuenow={value}
                    aria-valuemin={min}
                    aria-valuemax={max}
                    onFocus={() => {
                        focusedText.current = shown;
                        setDraft(shown);
                    }}
                    onBlur={() => {
                        commit();
                        setDraft(null);
                    }}
                    onKeyDown={(event) => {
                        // Su/Giù partono da ciò che è scritto, anche se non ancora consegnato, e il campo
                        // mostra il passo appena fatto.
                        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                            event.preventDefault();
                            const from = (draft === null ? null : parseDraft(draft)) ?? value;
                            const next = deliver(from + (event.key === 'ArrowUp' ? 1 : -1) * (step ?? 1));
                            setDraft(decimals === undefined ? String(next) : next.toFixed(decimals));
                        }
                        if (event.key === 'Enter') event.currentTarget.blur();
                        if (event.key === 'Escape') {
                            event.stopPropagation();
                            // Il valore di prima torna prima dell'uscita, così il blur non ha niente da consegnare.
                            setDraft(null);
                            const input = event.currentTarget;
                            requestAnimationFrame(() => input.blur());
                        }
                    }}
                    onChange={(event) => {
                        const text = event.target.value;
                        setDraft(text);
                        if (!live) return;
                        if (text.trim() === '') return onEmpty?.();
                        const parsed = parseDraft(text);
                        if (parsed !== null) onChange?.(clamp(parsed));
                    }}
                />
                {unit && <Unit>{unit}</Unit>}
            </Middle>
            <Step type='button' tabIndex={-1} aria-hidden='true' disabled={disabled} onClick={() => nudge(1)}>
                <ChevronRight />
            </Step>
        </Field>
    );
};
